import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    TextField,
    MenuItem,
    IconButton,
    Typography,
    Select,
    Chip,
} from "@mui/material";

import {
    Visibility,
    Delete,
    NotificationsActive,
    WhatsApp,
    Email,
    Sms,
} from "@mui/icons-material";
import Edit from "@mui/icons-material/Edit";

import { useNavigate } from "react-router-dom";
import deleteConfirm from "../emp/deleteEmp";

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/doctors";

function DoctorList() {
    const [doctors, setDoctors] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const navigate = useNavigate();

    /* =========================
       FETCH DOCTORS
    ========================= */
    const fetchDoctors = () => {
        axios
            .get(API_URL, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            })
            .then((res) => {
                setDoctors(res.data.data || []);
            })
            .catch((err) => {
                console.error("Error fetching doctors:", err);
            });
    };

    useEffect(() => {
        fetchDoctors();
    }, []);

    /* =========================
       FILTER
    ========================= */
    const filteredDoctors = doctors.filter((doc) => {
        const name = `${doc.firstName} ${doc.lastName}`.toLowerCase();
        const email = (doc.email || "").toLowerCase();
        const specialization = (doc.specialization || "").toLowerCase();

        return (
            (!statusFilter || doc.status === statusFilter) &&
            (name.includes(searchTerm.toLowerCase()) ||
                email.includes(searchTerm.toLowerCase()) ||
                specialization.includes(searchTerm.toLowerCase()))
        );
    });

    /* =========================
       LICENSE ALERT
    ========================= */
    const isLicenseExpiring = (licenses = []) => {
        const today = new Date();
        return licenses.some((l) => {
            if (!l.expiryDate) return false;
            const diff =
                (new Date(l.expiryDate) - today) / (1000 * 60 * 60 * 24);
            return diff <= 30;
        });
    };

    return (
        <Box p={3}>
            <Typography variant="h5" mb={2}>
                Doctors
            </Typography>

            {/* ACTION BAR */}
            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Button variant="contained" startIcon={<Email />}>
                    Send Email
                </Button>
                <Button variant="contained" color="warning" startIcon={<Sms />}>
                    Send SMS
                </Button>
                <Button variant="contained" color="success" startIcon={<WhatsApp />}>
                    Send WhatsApp
                </Button>

                <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    size="small"
                >
                    <MenuItem value="">All Status</MenuItem>
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                    <MenuItem value="ON_LEAVE">On Leave</MenuItem>
                </Select>

                <TextField
                    size="small"
                    label="Search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
            </Box>

            {/* TABLE */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: "#1976d2" }}>
                        <TableRow>
                            <TableCell sx={{ color: "white" }}>
                                <input type="checkbox" />
                            </TableCell>
                            <TableCell sx={{ color: "white" }}>Photo</TableCell>
                            <TableCell sx={{ color: "white" }}>Alert</TableCell>
                            <TableCell sx={{ color: "white" }}>Name</TableCell>
                            <TableCell sx={{ color: "white" }}>Email</TableCell>
                            <TableCell sx={{ color: "white" }}>Center</TableCell>
                            <TableCell sx={{ color: "white" }}>Code</TableCell>
                            <TableCell sx={{ color: "white" }}>Specialization</TableCell>
                            <TableCell sx={{ color: "white" }}>Department</TableCell>
                            <TableCell sx={{ color: "white" }}>Status</TableCell>
                            <TableCell sx={{ color: "white" }}>Log</TableCell>
                            <TableCell sx={{ color: "white" }}>Details</TableCell>
                            <TableCell sx={{ color: "white" }}>Edit</TableCell>
                            <TableCell sx={{ color: "white" }}>Delete</TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {filteredDoctors.map((doc) => (
                            <TableRow key={doc._id}>
                                {/* Check */}
                                <TableCell>
                                    <input type="checkbox" />
                                </TableCell>

                                {/* Photo */}
                                <TableCell>
                                    <Avatar
                                        src={
                                            doc.profileImage
                                                ? `https://api.hr-ebnhaian.cloud${doc.profileImage}`
                                                : "/default.jpg"
                                        }
                                        alt={`${doc.firstName} ${doc.lastName}`}
                                    />

                                </TableCell>

                                {/* Alert */}
                                <TableCell>
                                    {isLicenseExpiring(doc.medicalLicenses) ? (
                                        <NotificationsActive color="error" />
                                    ) : (
                                        "-"
                                    )}
                                </TableCell>

                                {/* Name */}
                                <TableCell>
                                    {doc.firstName} {doc.lastName}
                                </TableCell>

                                {/* Email */}
                                <TableCell>{doc.email || "-"}</TableCell>

                                {/* Center */}
                                <TableCell>{doc.center?.name || "-"}</TableCell>

                                {/* Code */}
                                <TableCell>{doc.doctorCode}</TableCell>

                                {/* Specialization */}
                                <TableCell>{doc.specialization}</TableCell>

                                {/* Department */}
                                <TableCell>{doc.department?.name || "-"}</TableCell>

                                {/* Status */}
                                <TableCell>
                                    <Chip
                                        label={doc.status}
                                        color={doc.status === "ACTIVE" ? "success" : "default"}
                                        size="small"
                                    />
                                </TableCell>

                                {/* Log */}
                                <TableCell>
                                    <IconButton>
                                        <Visibility />
                                    </IconButton>
                                </TableCell>

                                {/* Details */}
                                <TableCell>
                                    <IconButton onClick={() => navigate(`/doctors/${doc._id}`)}>
                                        📁
                                    </IconButton>
                                </TableCell>

                                {/* Edit */}
                                <TableCell>
                                    <IconButton
                                        color="primary"
                                        onClick={() => navigate(`/doctors/${doc._id}/edit`)}
                                    >
                                        <Edit />
                                    </IconButton>
                                </TableCell>

                                {/* Delete */}
                                <TableCell>
                                    <IconButton
                                        color="error"
                                        onClick={() =>
                                            deleteConfirm(`${API_URL}/${doc._id}`, fetchDoctors)
                                        }
                                    >
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}

                        {filteredDoctors.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={14} align="center">
                                    No doctors found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default DoctorList;
